import { useState } from 'react';
import styled from 'styled-components';
import { InputSearch } from 'ui/inputs/inputSearch';
import { TabsGroup, IndexTabsList, IndexTabTrigger } from 'ui/tabsGroup';
import { TableToggle } from 'ui/indexPages/tables/tableToggle';
import { backgroundColors, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { useTranslations } from '../../../hooks/useTranslations';
import { useFilterContext } from '../../../contexts/FilterContext';
import { useViewContext, CREATIVE_LIBRARY_VIEWS } from '../../../contexts/ViewContext';
import { FILE_TYPE_FILTERS } from '../../index/constants';
import UploadButtonCombo from '../../UploadButtonCombo';
import { usePanelContext } from '../../../contexts/Panel';
import { PanelIntent, PanelView } from 'modules/creativeLibrary';
import { usePaginationContext } from 'utils/contexts/pagination';
import { Breadcrumb } from 'ui/breadcrumb';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: ${backgroundColors.backgroundPrimary};
  margin: -${spacing.base} -${spacing.double} 0 -${spacing.double};
  padding: ${spacing.plusHalf};
  gap: ${spacing.plusHalf};
`;

const ControlsContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;

  > * {
    flex: 0 0 auto;
    margin-left: ${spacing.half};
  }
`;

const RightMeta = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

export const WideInputSearch = styled(InputSearch)`
  width: ${pxToRem(300)};
`;

const StyledTableToggle = styled(TableToggle)`
  height: ${spacing.doubleHalf};
  width: ${spacing.doubleHalf};
  padding: ${spacing.quarter};
`;

const testIds = {
  breadcrumb: 'breadcrumb',
};

export const PanelControls = () => {
  const { t } = useTranslations();
  const { fileType, setFileType, searchQuery, setSearchQuery } = useFilterContext();
  const [searchInputValue, setSearchInputValue] = useState(searchQuery);
  const { activeView, setActiveView, hasManageBaseTemplatesPermission } = useViewContext();
  const { setPanelView, setPanelIntent, panelIntent } = usePanelContext();
  const { setCurrentPage } = usePaginationContext();

  useDebouncedEffect(
    () => {
      setCurrentPage(1);
      setSearchQuery(searchInputValue);
    },
    [searchInputValue],
    {
      skipEffectOnMount: true,
      debounceTimeout: 500,
    },
  );

  return (
    <Container>
      {(panelIntent === PanelIntent.EditSelect || panelIntent === PanelIntent.Edit) && (
        <section data-test={testIds.breadcrumb}>
          <Breadcrumb
            isButton
            breadcrumb={{
              content: t('link.imageDetails'),
              onClick: () => setPanelView(PanelView.Detail),
            }}
            variant={PAGE_HEADER_VARIANTS.SECONDARY}
          />
        </section>
      )}
      <ControlsContainer data-test="panel-controls">
        <div data-test="left-section-controls">
          <TabsGroup
            data-test="panel-file-type-filter"
            defaultValue={fileType?.[0] || FILE_TYPE_FILTERS[0].value}
            onValueChange={(value) => {
              setCurrentPage(1);
              setFileType(value ? [value] : []);
            }}
          >
            <IndexTabsList>
              {FILE_TYPE_FILTERS.map((filter) => (
                <IndexTabTrigger key={filter.value} value={filter.value}>
                  {filter.label}
                </IndexTabTrigger>
              ))}
            </IndexTabsList>
          </TabsGroup>
        </div>
        <RightMeta data-test="right-section-controls">
          {hasManageBaseTemplatesPermission && (
            <UploadButtonCombo
              config={{
                label: t('button.upload'),
                onClick: () => {
                  setPanelView(PanelView.Upload);
                  setPanelIntent(PanelIntent.Upload);
                },
                actions: [
                  {
                    content: t('menu.linkRemote'),
                    id: 'btn-combo-import-from-url',
                    onClick: () => {
                      setPanelView(PanelView.Upload);
                      setPanelIntent(PanelIntent.ImportFromUrl);
                    },
                  },
                ],
              }}
            />
          )}
          <WideInputSearch
            data-test="panel-search-input"
            id="panel-search-input"
            onChange={setSearchInputValue}
            placeholder={t('input.search.placeholder')}
            value={searchInputValue}
          />
          <StyledTableToggle
            activeView={activeView}
            data-test="panel-view-toggle"
            setActiveView={setActiveView}
            views={CREATIVE_LIBRARY_VIEWS}
          />
        </RightMeta>
      </ControlsContainer>
    </Container>
  );
};
